import React, { useEffect, useState } from "react";
import { getMyProblems } from "../../../server/Exercises CRUD/Requests";
import { getTeacherDashboardStats } from "../../../server/Teacher-dashboard/requests";
import {
  ArrowDown,
  ArrowUp,
  Activity,
  BookOpen,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
} from "lucide-react";
import AvailabilityToggle from "./AvailabilityToggle";

// Palette raffinée
const COLORS = [
  "#4361EE",
  "#3A0CA3",
  "#F72585",
  "#4CC9F0",
  "#4895EF",
  "#560BAD",
  "#7209B7",
  "#B5179E",
  "#F15BB5",
  "#FEE440",
  "#00BBF9",
  "#00F5D4",
];

// Ajout des interfaces pour les props
interface StatCardProps {
  title: string;
  value: number;
  color: string;
  icon: React.ElementType;
}

interface PieChartProps {
  data: number[];
  labels: string[];
}

interface PieLegendProps {
  data: number[];
  labels: string[];
}

interface BarChartProps {
  status: Record<string, number>;
}

export interface TeacherDashboardStatsOutput {
  statusCount: {
    in_standby: number;
    in_processing: number;
    resolved: number;
    rejected: number;
  };
  subjectDistribution: Record<string, number>;
}

// Carte statistique
const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  color,
  icon: Icon,
}) => (
  <div
    className={`flex flex-col bg-white rounded-lg shadow-md p-6 transition-all duration-300 border-l-4 ${color} hover:shadow-lg`}
  >
    <div className="flex justify-between items-center mb-3">
      <div className="text-gray-500 text-sm font-medium">{title}</div>
      <Icon className="text-gray-400" size={18} />
    </div>
    <div className="text-3xl font-bold">
      {Number.isFinite(value) ? value : "Néant"}
    </div>
  </div>
);

// Camembert SVG
const PieChart: React.FC<PieChartProps> = ({ data, labels }) => {
  const total = data.reduce((a: number, b: number) => a + b, 0);
  let cumulative = 0;
  return (
    <div className="relative">
      <svg viewBox="0 0 100 100" className="w-56 h-56">
        <circle cx="50" cy="50" r="30" fill="white" />
        {data.map((value: number, i: number) => {
          const percentage = value / total;
          const startAngle = (cumulative / total) * 2 * Math.PI;
          const endAngle = ((cumulative + value) / total) * 2 * Math.PI;
          const x1 = 50 + 40 * Math.sin(startAngle);
          const y1 = 50 - 40 * Math.cos(startAngle);
          const x2 = 50 + 40 * Math.sin(endAngle);
          const y2 = 50 - 40 * Math.cos(endAngle);
          const largeArc = percentage > 0.5 ? 1 : 0;
          const pathData = `M50,50 L${x1},${y1} A40,40 0 ${largeArc} 1 ${x2},${y2} Z`;
          cumulative += value;
          return (
            <path
              key={i}
              d={pathData}
              fill={COLORS[i % COLORS.length]}
              stroke="white"
              strokeWidth="1"
              className="transition-all duration-500 hover:opacity-90"
            />
          );
        })}
        <text
          x="50"
          y="48"
          textAnchor="middle"
          className="text-2xl font-bold"
          fontSize="10"
        >
          {total}
        </text>
        <text
          x="50"
          y="58"
          textAnchor="middle"
          className="text-xs text-gray-400"
          fontSize="6"
        >
          Total
        </text>
      </svg>
    </div>
  );
};

// Légende camembert
const PieLegend: React.FC<PieLegendProps> = ({ data, labels }) => {
  const total = data.reduce((a: number, b: number) => a + b, 0);
  return (
    <div className="grid grid-cols-2 gap-3 mt-4">
      {labels.map((label: string, i: number) => (
        <div
          key={label}
          className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 transition-all"
        >
          <span
            className="inline-block w-4 h-4 rounded-full"
            style={{ backgroundColor: COLORS[i % COLORS.length] }}
          ></span>
          <div className="flex flex-col">
            <span className="font-medium text-sm">{label}</span>
            <div className="flex items-center space-x-1">
              <span className="text-gray-700 font-semibold">
                {Number.isFinite(data[i]) ? data[i] : "Néant"}
              </span>
              <span className="text-gray-400 text-xs">
                {total > 0 && Number.isFinite(data[i])
                  ? `(${((data[i] / total) * 100).toFixed(0)}%)`
                  : "(Néant)"}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Bar chart SVG
const BarChart: React.FC<BarChartProps> = ({ status }) => {
  const keys = ["in_standby", "in_processing", "resolved", "rejected"];
  const labels = ["En attente", "En cours", "Résolues", "Rejetées"];
  const values = keys.map((k) => status[k] || 0);
  const max = Math.max(...values, 1);
  const barColors = [
    "#FFC107",
    "#FF9800",
    "#4CAF50",
    "#F44336",
    "#3A0CA3",
    "#00BBF9",
  ];
  return (
    <div className="w-full flex flex-col items-center">
      <svg
        viewBox={`0 0 ${values.length * 45 + 20} 110`}
        className="w-full h-52"
      >
        {[0, 1, 2, 3, 4].map((i: number) => (
          <line
            key={`grid-${i}`}
            x1="0"
            y1={80 - i * 20}
            x2={values.length * 45 + 20}
            y2={80 - i * 20}
            stroke="#f0f0f0"
            strokeWidth="1"
          />
        ))}
        {values.map((v: number, i: number) => (
          <g key={i} className="transition-all duration-500">
            <rect
              x={15 + i * 45}
              y={80 - (v / max) * 70}
              width={30}
              height={(v / max) * 70}
              fill={barColors[i % barColors.length]}
              rx={4}
              className="transition-all duration-500 hover:opacity-90"
            />
            <text
              x={30 + i * 45}
              y={95}
              textAnchor="middle"
              fontSize="8"
              fill="#555"
              fontWeight="500"
            >
              {labels[i]}
            </text>
            <text
              x={30 + i * 45}
              y={80 - (v / max) * 70 - 8}
              textAnchor="middle"
              fontSize="10"
              fill="#333"
              fontWeight="bold"
            >
              {Number.isFinite(v) ? v : "Néant"}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState<TeacherDashboardStatsOutput | null>(null);
  const [recentProblems, setRecentProblems] = useState<
    {
      id: string;
      title: string;
      status: string;
    }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [statsResponse, problemsResponse] = await Promise.all([
          getTeacherDashboardStats(),
          getMyProblems(),
        ]);
        if (statsResponse.status === 200 && statsResponse.body) {
          setStats(statsResponse.body as TeacherDashboardStatsOutput);
        } else {
          throw new Error("Erreur lors de la récupération des statistiques");
        }
        if (
          problemsResponse.status === 200 &&
          Array.isArray(problemsResponse.body)
        ) {
          const recent = problemsResponse.body
            .sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            )
            .slice(0, 5)
            .map((p) => ({
              id: p._id,
              title: p.matiere,
              status: p.status,
            }));
          setRecentProblems(recent);
        }
      } catch (err) {
        setError("Une erreur est survenue lors de la récupération des données");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium">Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="flex flex-col items-center justify-center h-96 p-6 bg-white rounded-lg shadow-md">
        <AlertCircle size={48} className="text-red-500 mb-4" />
        <div className="text-xl font-bold text-red-500">
          {error || "Aucune donnée disponible."}
        </div>
        <p className="text-gray-500 mt-2">
          Veuillez réessayer ultérieurement ou contacter le support.
        </p>
      </div>
    );
  }

  const { statusCount, subjectDistribution } = stats;
  const labels = Object.keys(subjectDistribution);
  const data = Object.values(subjectDistribution).map(Number);
  const totalSubmissions = Object.values(statusCount).reduce(
    (a: number, b: number) => a + b,
    0,
  );

  return (
    <div className="max-w-6xl mx-auto py-10 px-4 space-y-8">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 bg-white p-6 rounded-lg shadow-md">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Tableau de bord enseignant
          </h1>
          <p className="text-gray-500 mt-1">
            Suivez la progression des élèves et la répartition des exercices
          </p>
        </div>
        <AvailabilityToggle />
      </div>

      {/* Résumé */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-bold text-gray-800 mb-2">Résumé</h2>
        <p className="text-gray-600">
          Aperçu global des soumissions et de la répartition par matière.
        </p>
      </div>

      {/* Statistiques principales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total des soumissions"
          value={totalSubmissions}
          color="border-blue-500"
          icon={Activity}
        />
        <StatCard
          title="En attente"
          value={statusCount.in_standby}
          color="border-yellow-500"
          icon={Clock}
        />
        <StatCard
          title="En cours"
          value={statusCount.in_processing}
          color="border-orange-500"
          icon={BookOpen}
        />
        <StatCard
          title="Résolues"
          value={statusCount.resolved}
          color="border-green-500"
          icon={CheckCircle}
        />
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Diagramme en bande (bar chart) */}
        <div className="bg-white rounded-lg shadow-md p-6 flex flex-col transition-all duration-300 hover:shadow-lg">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-800">
              Soumissions par statut
            </h2>
            <Activity size={20} className="text-gray-400" />
          </div>
          <BarChart status={statusCount} />
        </div>
        {/* Distribution par matière */}
        <div className="bg-white rounded-lg shadow-md p-6 flex flex-col transition-all duration-300 hover:shadow-lg">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-800">
              Distribution par matière
            </h2>
            <BookOpen size={20} className="text-gray-400" />
          </div>
          <div className="flex flex-col md:flex-row items-center justify-around">
            {data.length > 0 ? (
              <>
                <PieChart data={data} labels={labels} />
                <PieLegend data={data} labels={labels} />
              </>
            ) : (
              <div className="flex flex-col items-center text-gray-400 py-12">
                <AlertCircle size={36} className="mb-4 text-gray-300" />
                <p>Aucune donnée de matière disponible.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tendances des soumissions */}
      <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-gray-800">
            Tendances des soumissions
          </h2>
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-green-600 flex items-center">
              <ArrowUp size={16} className="mr-1" /> {statusCount.resolved}{" "}
              résolues
            </span>
            <span className="text-sm font-medium text-red-600 flex items-center ml-4">
              <ArrowDown size={16} className="mr-1" /> {statusCount.rejected}{" "}
              rejetées
            </span>
          </div>
        </div>
        <div className="relative overflow-x-auto rounded-lg">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3">
                  Statut
                </th>
                <th scope="col" className="px-6 py-3">
                  Nombre
                </th>
                <th scope="col" className="px-6 py-3">
                  Pourcentage
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white border-b">
                <td className="px-6 py-4 flex items-center">
                  <Clock size={16} className="mr-2 text-yellow-500" />
                  <span>En attente</span>
                </td>
                <td className="px-6 py-4 font-medium">
                  {statusCount.in_standby}
                </td>
                <td className="px-6 py-4">
                  {totalSubmissions > 0
                    ? `${(
                        (statusCount.in_standby / totalSubmissions) *
                        100
                      ).toFixed(1)}%`
                    : "Néant"}
                </td>
              </tr>
              <tr className="bg-white border-b">
                <td className="px-6 py-4 flex items-center">
                  <BookOpen size={16} className="mr-2 text-orange-500" />
                  <span>En cours</span>
                </td>
                <td className="px-6 py-4 font-medium">
                  {statusCount.in_processing}
                </td>
                <td className="px-6 py-4">
                  {totalSubmissions > 0
                    ? `${(
                        (statusCount.in_processing / totalSubmissions) *
                        100
                      ).toFixed(1)}%`
                    : "Néant"}
                </td>
              </tr>
              <tr className="bg-white border-b">
                <td className="px-6 py-4 flex items-center">
                  <CheckCircle size={16} className="mr-2 text-green-500" />
                  <span>Résolues</span>
                </td>
                <td className="px-6 py-4 font-medium">
                  {statusCount.resolved}
                </td>
                <td className="px-6 py-4">
                  {totalSubmissions > 0
                    ? `${(
                        (statusCount.resolved / totalSubmissions) *
                        100
                      ).toFixed(1)}%`
                    : "Néant"}
                </td>
              </tr>
              <tr className="bg-white">
                <td className="px-6 py-4 flex items-center">
                  <XCircle size={16} className="mr-2 text-red-500" />
                  <span>Rejetées</span>
                </td>
                <td className="px-6 py-4 font-medium">
                  {statusCount.rejected}
                </td>
                <td className="px-6 py-4">
                  {totalSubmissions > 0
                    ? `${(
                        (statusCount.rejected / totalSubmissions) *
                        100
                      ).toFixed(1)}%`
                    : "Néant"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
